/**
 * Leitura paginada e recortada do Relatório de Pagamentos.
 *
 * Não reutiliza `useFinanceEntriesRaw`: aquela leitura existe para o módulo
 * Financeiro e não pagina nem recebe as dimensões do relatório. Aqui período,
 * cliente, status e classificação entram no PostgREST antes de `.range()`.
 */
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import { fetchAllPages } from '@/lib/reportPaging';
import { getCachedFinanceEntries } from '@/lib/offlineDataCache';
import {
  financeEntryMatchesReportFilters,
  type ReportFilterState,
} from '@/lib/reportFilters';
import {
  paymentSortServerColumn,
  sortPaymentEntries,
  type IsoDateRange,
  type PaymentDateBasis,
  type PaymentsReportSort,
} from '@/lib/paymentsReport';
import type { FinanceEntryWithStatus } from '@/types/database';

export interface ReportPaymentsOptions {
  range: IsoDateRange;
  basis: PaymentDateBasis;
  filters: ReportFilterState;
  sort: PaymentsReportSort;
  enabled: boolean;
}

export interface ReportPaymentsResult {
  entries: FinanceEntryWithStatus[];
  truncated: boolean;
}

function cachedEntries(
  rows: readonly FinanceEntryWithStatus[],
  options: Omit<ReportPaymentsOptions, 'enabled'>,
): FinanceEntryWithStatus[] {
  return sortPaymentEntries(
    rows.filter((row) =>
      financeEntryMatchesReportFilters(row, options.range, options.basis, options.filters)),
    options.sort,
    options.basis,
  );
}

export function useReportPayments(options: ReportPaymentsOptions) {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const { range, basis, filters, sort, enabled } = options;
  const clientId = filters.clientId;
  const status = filters.statusByTab.payments;
  const sortColumn = paymentSortServerColumn(sort.field, basis);

  const query = useQuery<ReportPaymentsResult>({
    queryKey: [
      'report-payments',
      companyId,
      range.from,
      range.to,
      basis,
      clientId,
      status,
      sort.field,
      sort.direction,
    ],
    enabled: Boolean(user?.id && companyId) && enabled,
    networkMode: 'offlineFirst',
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem), então
    // `refetchOnMount` já passou quando ela habilita: sem o carimbo, o cache semeado
    // conta como FRESCO e a primeira busca nunca sai (#158).
    initialDataUpdatedAt: 0,
    initialData: () => {
      if (!user?.id || !companyId) return undefined;
      const cache = (getCachedFinanceEntries(user.id) ?? [])
        .filter((row: any) => !row?.company_id || row.company_id === companyId);
      return {
        entries: cachedEntries(cache as FinanceEntryWithStatus[], {
          range,
          basis,
          filters,
          sort,
        }),
        truncated: false,
      };
    },
    queryFn: async () => {
      if (!companyId) return { entries: [], truncated: false };

      const result = await fetchAllPages<FinanceEntryWithStatus>((start, end) => {
        let query: any = supabase
          .from('finance_entries_with_status')
          .select('*')
          .eq('company_id', companyId);

        // Base realizada precisa também dos vencimentos do período para os KPIs
        // "A receber" e "Vencido". A união acontece NO SERVIDOR, antes da página.
        if (basis === 'paid') {
          query = query.or(
            `and(paid_date.gte.${range.from},paid_date.lte.${range.to}),`
            + `and(due_date.gte.${range.from},due_date.lte.${range.to})`,
          );
        } else {
          query = query.gte('due_date', range.from).lte('due_date', range.to);
        }
        if (clientId) query = query.eq('client_id', clientId);
        if (status) query = query.eq('computed_status', status);

        return query
          .order(sortColumn, { ascending: sort.direction === 'asc', nullsFirst: false })
          .order('id', { ascending: true })
          .range(start, end);
      });

      return {
        // Cinto e suspensório/offline parity: o predicado puro afirma a mesma
        // expressão da query e preserva o espelho local de `computed_status`.
        entries: cachedEntries(result.rows, { range, basis, filters, sort }),
        truncated: result.truncated,
      };
    },
  });

  return {
    entries: query.data?.entries ?? [],
    truncated: query.data?.truncated ?? false,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
